"use client";

import React from "react";
import { MapPin, Phone, Mail, Terminal, ShieldAlert, Cpu } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
const contactInfo = [{
  icon: MapPin,
  label: "Our Office",
  value: "Rapid City, SD",
  description: "Serving the Black Hills Region"
}, {
  icon: Phone,
  label: "Call Us",
  value: "(605) 910-4836",
  description: "Mon-Fri, 8am - 5pm"
}, {
  icon: Mail,
  label: "Email Support",
  value: "info@rapidtechworks.com",
  description: "We respond within 24 hours"
}];
const inputClasses = "flex w-full bg-transparent border-0 border-b border-border py-3 text-base text-foreground placeholder:text-muted-foreground/40 focus-visible:outline-none focus-visible:border-primary focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 transition-colors duration-200 rounded-none font-default";
function ExpeditionFormInternal() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center p-8 text-center border border-primary/30 bg-primary/5 rounded-2xl">
        <ShieldAlert className="size-12 text-primary mb-4" />
        <h3 className="text-2xl font-default font-bold text-foreground mb-2">
          Message Received
        </h3>
        <p className="text-muted-foreground font-default mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="font-default border-primary/50 hover:bg-primary/10 hover:text-primary rounded-full">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="space-y-6">
        <FormField control={control} name="full_name" rules={{
        required: "Please provide your name."
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs text-primary/70 tracking-widest uppercase">
                // full-name
              </FormLabel>
              <FormControl>
                <input {...field} type="text" placeholder="John Smith" className={inputClasses} />
              </FormControl>
              <FormDescription className="font-default text-xs opacity-70">
                How should we address you?
              </FormDescription>
              <FormMessage className="font-default text-xs text-destructive" />
            </FormItem>} />

        <FormField control={control} name="email_address" rules={{
        required: "Email is required.",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address."
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs text-primary/70 tracking-widest uppercase">
                // business-email
              </FormLabel>
              <FormControl>
                <input {...field} type="email" placeholder="john@company.com" className={inputClasses} />
              </FormControl>
              <FormDescription className="font-default text-xs opacity-70">
                We'll use this to get back to you.
              </FormDescription>
              <FormMessage className="font-default text-xs text-destructive" />
            </FormItem>} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="age" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs text-primary/70 tracking-widest uppercase">
                  // phone-number
                </FormLabel>
                <FormControl>
                  <input {...field} type="text" placeholder="(605) 000-0000" className={inputClasses} />
                </FormControl>
                <FormDescription className="font-default text-xs opacity-70">
                  Optional for quicker contact.
                </FormDescription>
                <FormMessage className="font-default text-xs text-destructive" />
              </FormItem>} />

          <FormField control={control} name="education_level" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-mono text-xs text-primary/70 tracking-widest uppercase">
                  // service-interest
                </FormLabel>
                <FormControl>
                  <select {...field} className={`${inputClasses} appearance-none cursor-pointer`}>
                    <option value="" disabled className="bg-background text-muted-foreground">
                      Select a service...
                    </option>
                    {["Managed IT", "Cybersecurity", "Cabling & Infrastructure", "Cloud Solutions", "Other"].map((opt, i) => <option key={i} value={opt} className="bg-background text-foreground">
                          {opt}
                        </option>)}
                  </select>
                </FormControl>
                <FormDescription className="font-default text-xs opacity-70">
                  What can we help you with?
                </FormDescription>
                <FormMessage className="font-default text-xs text-destructive" />
              </FormItem>} />
        </div>

        <FormField control={control} name="interest_message" rules={{
        maxLength: {
          value: 500,
          message: "Message is too long."
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs text-primary/70 tracking-widest uppercase">
                // message
              </FormLabel>
              <FormControl>
                <textarea {...field} placeholder="Tell us about your IT challenges or project goals..." className={`${inputClasses} min-h-[120px] resize-none`} />
              </FormControl>
              <FormMessage className="font-default text-xs text-destructive" />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 border border-destructive/50 bg-destructive/5 rounded-lg">
          <p className="text-destructive font-default text-sm flex items-center gap-2">
            <Terminal className="size-4" />
            Error: {submitError}
          </p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 font-mono text-sm font-bold tracking-widest uppercase relative overflow-hidden group bg-primary text-primary-foreground hover:bg-primary/90 rounded-full shadow-md hover:shadow-lg transition-all duration-300">
        <span className="relative z-10 flex items-center gap-2">
          {isSubmitting ? <>
              <Cpu className="animate-spin size-5" />
              sending...
            </> : <>
              <Terminal className="size-5" />
              $ send --message
            </>}
        </span>
        <div className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-in-out" />
      </Button>
    </Form>;
}
export default function JoinTheExpedition() {
  return <section id="lets-talk-tech" className="bg-background text-foreground py-24 px-4 relative overflow-hidden">
      {/* original decorative lines */}
      <div className="absolute inset-0 pointer-events-none opacity-30">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-secondary/20 to-transparent" />
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-secondary/20 to-transparent" />
        <div className="absolute left-[10%] top-0 w-px h-full bg-gradient-to-b from-transparent via-secondary/10 to-transparent" />
        <div className="absolute right-[10%] top-0 w-px h-full bg-gradient-to-b from-transparent via-secondary/10 to-transparent" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-24 items-start">

          {/* left */}
          <div className="lg:col-span-2 space-y-10">
            <div>
              <div className="font-mono text-xs text-primary/70 tracking-widest mb-4">
                // lets-talk-tech
              </div>
              <h2 className="text-4xl md:text-5xl font-default font-bold text-foreground mb-4">
                Let's Talk<br />Tech
              </h2>
              <p className="text-muted-foreground font-default text-lg leading-relaxed mb-6">
                Ready to upgrade your business IT? We're passionate about helping local Rapid Valley businesses thrive with reliable, professional technology solutions.
              </p>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-secondary/20 bg-secondary/5 font-mono text-xs text-secondary/80">
                <ShieldAlert className="size-3.5" />
                <span>local-support --available</span>
              </div>
            </div>

            <div className="space-y-6">
              {contactInfo.map((item, index) => <div key={index} data-index={index} className="flex items-start gap-4 group">
                  <div className="p-2.5 rounded-xl bg-secondary/10 border border-secondary/10 text-secondary group-hover:bg-secondary group-hover:text-secondary-foreground transition-all duration-300 shadow-sm shrink-0">
                    <item.icon className="size-5" />
                  </div>
                  <div>
                    <div className="font-mono text-xs text-primary/60 tracking-widest mb-0.5">
                      // {item.label.toLowerCase().replace(" ", "-")}
                    </div>
                    <p className="text-base font-semibold text-foreground font-default mb-0.5">
                      {item.value}
                    </p>
                    <p className="text-sm text-muted-foreground font-default">{item.description}</p>
                  </div>
                </div>)}
            </div>
          </div>

          {/* right — floating blended card */}
          <div className="lg:col-span-3">
            <div className="relative">
              {/* floating glow layer */}
              <div className="absolute -inset-px rounded-3xl bg-gradient-to-br from-primary/20 via-transparent to-secondary/20 opacity-60 blur-sm pointer-events-none" />
              <div className="absolute -inset-3 rounded-3xl bg-primary/5 blur-2xl opacity-50 pointer-events-none" />

              <div className="relative bg-card backdrop-blur-sm border border-border/60 rounded-3xl shadow-[0_8px_32px_rgba(0,0,0,0.12),0_24px_64px_-16px_rgba(0,0,0,0.18),0_0_0_1px_rgba(255,255,255,0.04)]">
                {/* subtle terminal title strip */}
                <div className="flex items-center gap-2 px-6 py-3.5 border-b border-border/40">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-primary/40 border border-primary/20" />
                    <span className="w-2.5 h-2.5 rounded-full bg-secondary/40 border border-secondary/20" />
                    <span className="w-2.5 h-2.5 rounded-full bg-muted-foreground/20 border border-muted-foreground/10" />
                  </div>
                  <span className="font-mono text-xs text-muted-foreground/50 ml-2">
                    rtw:/contact --interactive
                  </span>
                </div>

                <div className="p-8 md:p-12">
                  {/* corner accents */}
                  <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-primary/30 rounded-tl-3xl" />
                  <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-primary/30 rounded-tr-3xl" />
                  <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-primary/30 rounded-bl-3xl" />
                  <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-primary/30 rounded-br-3xl" />

                  <div className="mb-8 border-b border-border/50 pb-6">
                    <h3 className="text-2xl font-default font-bold text-foreground flex items-center gap-3">
                      <Terminal className="size-6 text-primary" />
                      Start a Conversation
                    </h3>
                    <p className="font-mono text-xs text-muted-foreground/60 mt-2">
                      $ fill out the form &mdash; our team will reach out shortly
                    </p>
                  </div>

                  <FormProvider formKey="talk_tech_form_contact" formId="talk_tech_form_contact" sectionName="Let's Talk Tech" defaultValues={{
                  full_name: "",
                  email_address: "",
                  age: "",
                  education_level: "",
                  interest_message: ""
                }} submitText="Send Message" successMessage="Thanks for reaching out! We've received your message and one of our experts will be in touch soon.">
                    <ExpeditionFormInternal />
                  </FormProvider>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}